<!-- app/javascript/dashboard/routes/dashboard/bulkmessage/components/MessageTemplate.vue -->

<script setup>
import { ref, computed, watch } from 'vue';
import axios from 'axios';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import WhatsAppPreview from './MessagePreview/WhatsAppPreview.vue';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  selectedTemplate: {
    type: Object,
    default: null,
  },
  selectedInboxId: {
    type: [String, Number],
    default: null,
  },
  additionalColumns: {
    type: Array,
    default: () => [],
  },
  recipients: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['select-template']);

// State
const templates = ref([]);
const isLoading = ref(false);
const isSyncing = ref(false);
const searchQuery = ref('');
const activeLanguage = ref('pt_BR');
const selectedCategory = ref('all');
const lastUpdated = ref(null);
const canSync = ref(true);
const nextSyncAt = ref(null);
const cooldownTimer = ref(null);
const cooldownSeconds = ref(0);
const showTemplateVariables = ref(false);
const parameterErrors = ref({});

// Media state for header with media
const mediaInputType = ref('upload'); // 'url' | 'upload'
const mediaUrl = ref('');
const mediaFile = ref(null);
const mediaPreviewUrl = ref('');
const isUploading = ref(false);

// Template variables like in MessageComposer - now computed to include custom columns
const templateVariables = computed(() => {
  // Built-in variables
  const builtInVariables = [
    {
      name: `{{nome}}`,
      description: 'Nome do contato (primeira parte)',
    },
    {
      name: `{{sobrenome}}`,
      description: 'Sobrenome do contato (segunda parte)',
    },
    { name: `{{nome_completo}}`, description: 'Nome completo do contato' },
    { name: `{{telefone}}`, description: 'Número de telefone do contato' },
  ];

  // Add custom columns from CSV import
  const customVariables = (props.additionalColumns || [])
    .filter(column => {
      // Exclude DDI/country code columns
      const lowerColumn = column.toLowerCase();
      return !lowerColumn.includes('ddi') && !lowerColumn.includes('country');
    })
    .map(column => ({
      name: `{{${column}}}`,
      description: `Campo personalizado: ${column}`,
    }));

  return [...builtInVariables, ...customVariables];
});

// Template categories
const categories = [
  { id: 'all', name: 'Todas as Categorias' },
  { id: 'MARKETING', name: 'Marketing' },
  { id: 'UTILITY', name: 'Utilitário' },
  { id: 'AUTHENTICATION', name: 'Autenticação' },
];

// Languages supported by WhatsApp
const languages = [
  { code: 'all', name: 'Todos os idiomas' },
  { code: 'pt_BR', name: 'Português (BR)' },
  { code: 'en_US', name: 'English (US)' },
  { code: 'es_ES', name: 'Español' },
  { code: 'fr_FR', name: 'Français' },
];

// Filtered templates based on search, language and category
const filteredTemplates = computed(() => {
  let result = templates.value;

  // Filter by language
  if (activeLanguage.value !== 'all') {
    result = result.filter(t => t.language === activeLanguage.value);
  }

  // Filter by category
  if (selectedCategory.value !== 'all') {
    result = result.filter(t => t.category === selectedCategory.value);
  }

  // Filter by search query
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    result = result.filter(
      t =>
        t.name.toLowerCase().includes(query) ||
        getTemplateContent(t).toLowerCase().includes(query)
    );
  }

  return result;
});

// Detect header with media in selected template
const headerComponent = computed(() => {
  return props.selectedTemplate?.components?.find(c => c.type === 'HEADER');
});

const headerFormat = computed(() => {
  return headerComponent.value?.format || null;
});

const hasMediaHeader = computed(() => {
  return ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerFormat.value);
});

const mediaTypeLabel = computed(() => {
  const labels = {
    IMAGE: 'Imagem',
    VIDEO: 'Vídeo',
    DOCUMENT: 'Documento',
  };
  return labels[headerFormat.value] || '';
});

const mediaIcon = computed(() => {
  const icons = {
    IMAGE: 'i-lucide-image',
    VIDEO: 'i-lucide-video',
    DOCUMENT: 'i-lucide-file-text',
  };
  return icons[headerFormat.value] || 'i-lucide-file';
});

const acceptedFileTypes = computed(() => {
  const types = {
    IMAGE: 'image/jpeg,image/png,image/webp',
    VIDEO: 'video/mp4,video/3gpp',
    DOCUMENT:
      'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  };
  return types[headerFormat.value] || '*/*';
});

const isValidHttpUrl = url => /^https?:\/\//i.test(url || '');

const currentMediaValue = computed(() => {
  if (mediaInputType.value === 'url') {
    return mediaUrl.value;
  }
  return mediaPreviewUrl.value;
});

const isMediaValid = computed(() => {
  if (!hasMediaHeader.value) return true;
  if (mediaInputType.value === 'upload') {
    return !!mediaFile.value;
  }

  const value = currentMediaValue.value?.trim();
  if (!value) return false;
  return isValidHttpUrl(value);
});

// Whether the template is selected
const isTemplateSelected = templateId => {
  if (!props.selectedTemplate) return false;
  // Handle both string and number comparison
  return String(props.selectedTemplate.id) === String(templateId);
};

// Get template content from components
const getTemplateContent = template => {
  if (!template.components) return '';

  let content = '';
  template.components.forEach(component => {
    if (component.type === 'HEADER' && component.text) {
      content += `**${component.text}**\n\n`;
    } else if (component.type === 'BODY' && component.text) {
      content += component.text + '\n\n';
    } else if (component.type === 'FOOTER' && component.text) {
      content += `_${component.text}_`;
    }
  });

  return content.trim();
};

// Get template parameters from body component
const getTemplateParameters = template => {
  if (!template.components) return [];

  const bodyComponent = template.components.find(c => c.type === 'BODY');
  if (!bodyComponent || !bodyComponent.text) return [];

  // Extract parameters from {{1}}, {{2}}, etc.
  const matches = bodyComponent.text.match(/\{\{(\d+)\}\}/g);
  if (!matches) return [];

  return matches.map((match, index) => {
    const paramNumber = match.replace(/\{\{|\}\}/g, '');
    const example = bodyComponent.example?.body_text?.[0]?.[index] || '';

    return {
      index: parseInt(paramNumber),
      name: `param_${paramNumber}`,
      type: 'text',
      placeholder: `Varíável ${paramNumber}`,
      example: example,
      value: '',
    };
  });
};

// Get template buttons
const getTemplateButtons = template => {
  if (!template.components) return [];

  const buttonsComponent = template.components.find(c => c.type === 'BUTTONS');
  if (!buttonsComponent || !buttonsComponent.buttons) return [];

  return buttonsComponent.buttons;
};

// Format template preview with parameters
const formatTemplatePreview = template => {
  let content = getTemplateContent(template);
  const parameters = getTemplateParameters(template);

  parameters.forEach(param => {
    const placeholder = `{{${param.index}}}`;
    // Process template variables in the parameter value, then use it
    const processedValue = processVariablesInText(
      param.value || param.example || `{${param.index}}`
    );
    content = content.replace(new RegExp(placeholder, 'g'), processedValue);
  });

  // Apply WhatsApp formatting but convert to plain text for WhatsApp preview
  // This will be handled by the WhatsApp preview component
  return content;
};

// Removed unused getStatusBadgeClass function

// Get enhanced status badge class with better colors
const getEnhancedStatusBadgeClass = status => {
  switch (status) {
    case 'APPROVED':
      return 'bg-green-50/35 text-green-700 dark:bg-green-900/30 dark:text-green-300';
    case 'PENDING':
      return 'bg-amber-50 text-amber-700 dark:bg-amber-100 dark:text-amber-600';
    case 'REJECTED':
      return 'bg-red-50/50 text-red-700 dark:bg-ruby-100 dark:text-ruby-600';
    default:
      return 'bg-gray-50 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300';
  }
};

// Get status icon
const getStatusIcon = status => {
  switch (status) {
    case 'APPROVED':
      return 'i-lucide-check-circle';
    case 'PENDING':
      return 'i-lucide-clock';
    case 'REJECTED':
      return 'i-lucide-x-circle';
    default:
      return 'i-lucide-help-circle';
  }
};

// Translate status to Portuguese BR
const translateStatus = status => {
  switch (status) {
    case 'APPROVED':
      return 'APROVADO';
    case 'PENDING':
      return 'PENDENTE';
    case 'REJECTED':
      return 'REJEITADO';
    default:
      return status;
  }
};

// Translate category to Portuguese BR (uppercase)
const translateCategory = category => {
  switch (category) {
    case 'MARKETING':
      return 'MARKETING';
    case 'UTILITY':
      return 'UTILIDADE';
    case 'AUTHENTICATION':
      return 'AUTENTICAÇÃO';
    default:
      return category;
  }
};


// Helper functions for authentication - SAME AS BulkMessage.vue
const getCookie = name => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
};

const getAuthHeaders = () => {
  const cookie = getCookie('cw_d_session_info');
  if (!cookie) return {};
  try {
    const sessionInfo = JSON.parse(cookie);
    return {
      'access-token': sessionInfo['access-token'],
      client: sessionInfo.client,
      uid: sessionInfo.uid,
    };
  } catch (e) {
    console.error('Erro ao ler o cookie:', e);
    return {};
  }
};

// Fetch templates from API - UPDATED TO USE AXIOS LIKE BulkMessage.vue
const fetchTemplates = async () => {
  if (!props.selectedInboxId) {
    templates.value = [];
    return;
  }

  isLoading.value = true;

  try {
    const response = await axios.get(
      `/api/v1/bulk_messages/whatsapp_templates/${props.selectedInboxId}`,
      {
        headers: getAuthHeaders(),
        withCredentials: true,
      }
    );

    if (response.data) {
      // Process templates from database format to component format
      const processedTemplates = (response.data.templates || []).map(
        template => ({
          id: template.id,
          name: template.name,
          status: template.status || 'APPROVED',
          category: template.category || 'UTILITY',
          language: template.language || 'pt_BR',
          components: template.components || [],
          sub_category: template.sub_category,
          parameter_format: template.parameter_format || 'POSITIONAL',
          message_send_ttl_seconds: template.message_send_ttl_seconds,
        })
      );

      templates.value = processedTemplates;
      lastUpdated.value = response.data.last_updated;
      canSync.value = response.data.can_sync;
      nextSyncAt.value = response.data.next_sync_at;

      // Start cooldown timer if needed
      if (!canSync.value) {
        startCooldownTimer();
      }
    }
  } catch (error) {
    console.error('Failed to fetch templates:', error);
    useAlert('Erro ao carregar templates', 'error');
  } finally {
    isLoading.value = false;
  }
};

// Sync templates manually - UPDATED TO USE AXIOS
const syncTemplates = async () => {
  if (!props.selectedInboxId || !canSync.value) return;

  isSyncing.value = true;

  try {
    const response = await axios.post(
      `/api/v1/bulk_messages/whatsapp_templates/${props.selectedInboxId}/sync`,
      {},
      {
        headers: getAuthHeaders(),
        withCredentials: true,
      }
    );

    if (response.data) {
      useAlert('Templates sincronizados', 'success');

      // Update sync status
      canSync.value = false;
      nextSyncAt.value = response.data.next_sync_at;
      startCooldownTimer();

      // Refresh templates after a delay
      setTimeout(() => {
        fetchTemplates();
      }, 2000);
    }
  } catch (error) {
    console.error('Failed to sync templates:', error);

    if (error.response?.status === 429) {
      // Handle cooldown
      const errorData = error.response.data;
      canSync.value = false;
      cooldownSeconds.value = errorData.remaining_seconds || 60;
      nextSyncAt.value = errorData.next_sync_at;
      startCooldownTimer();

      useAlert(
        `Aguarde ${errorData.remaining_seconds || 60}s para sincronizar`,
        'warning'
      );
    } else {
      useAlert('Erro ao sincronizar templates', 'error');
    }
  } finally {
    isSyncing.value = false;
  }
};

// Start cooldown timer
const startCooldownTimer = () => {
  if (cooldownTimer.value) {
    clearInterval(cooldownTimer.value);
  }

  const updateCooldown = () => {
    if (nextSyncAt.value) {
      const now = new Date();
      const nextSync = new Date(nextSyncAt.value);
      const remaining = Math.max(0, Math.ceil((nextSync - now) / 1000));

      cooldownSeconds.value = remaining;

      if (remaining <= 0) {
        canSync.value = true;
        clearInterval(cooldownTimer.value);
        cooldownTimer.value = null;
      }
    }
  };

  updateCooldown();
  cooldownTimer.value = setInterval(updateCooldown, 1000);
};

// Select a template
const selectTemplate = template => {
  // Add parameters to template for easier handling
  const templateWithParams = {
    ...template,
    parameters: getTemplateParameters(template),
  };

  // Emit the selected template
  emit('select-template', templateWithParams);
};

// Format date for display
const formatDate = dateString => {
  if (!dateString) return 'Nunca';
  return new Date(dateString).toLocaleString();
};

// Media handling functions
const handleFileSelect = event => {
  const file = event.target.files[0];
  if (!file) return;

  const maxSize = 20 * 1024 * 1024;
  if (file.size > maxSize) {
    useAlert('O arquivo deve ter no máximo 20MB');
    return;
  }

  mediaFile.value = file;

  if (headerFormat.value === 'IMAGE' || headerFormat.value === 'VIDEO') {
    mediaPreviewUrl.value = URL.createObjectURL(file);
  }
};

const clearMedia = () => {
  mediaFile.value = null;
  mediaUrl.value = '';
  mediaPreviewUrl.value = '';
  mediaInputType.value = 'url';
};

// Clear all parameter values
const clearAllParameters = () => {
  if (props.selectedTemplate?.parameters) {
    props.selectedTemplate.parameters.forEach(param => {
      param.value = '';
    });
  }
};

// Fill all parameters with example values
const fillExampleParameters = () => {
  if (props.selectedTemplate?.parameters) {
    props.selectedTemplate.parameters.forEach(param => {
      if (param.example) {
        param.value = param.example;
      }
    });
  }
};

// Insert template variable into a parameter field
const insertVariable = (variable, paramIndex) => {
  if (
    props.selectedTemplate?.parameters &&
    props.selectedTemplate.parameters[paramIndex]
  ) {
    const param = props.selectedTemplate.parameters[paramIndex];
    const currentValue = param.value || '';
    param.value = currentValue + variable;
  }
  // Hide variables dropdown
  showTemplateVariables.value = false;
};

// Process template variables in parameter values for preview (like MessageComposer.vue)
const processVariablesInText = text => {
  if (!text) return text;

  let processed = text;

  // Replace template variables with sample values (same logic as MessageComposer.vue)
  processed = processed.replace(/\{\{nome\}\}/g, 'João');
  processed = processed.replace(/\{\{sobrenome\}\}/g, 'Silva');
  processed = processed.replace(/\{\{nome_completo\}\}/g, 'João Silva');
  processed = processed.replace(/\{\{telefone\}\}/g, '+55 11 99999-9999');

  return processed;
};

// Computed property for processed template preview (reactive to parameter changes)
const processedTemplatePreview = computed(() => {
  if (!props.selectedTemplate) return '';
  return formatTemplatePreview(props.selectedTemplate);
});

// Validate template parameters
const validateParameters = () => {
  const errors = {};
  let isValid = true;

  if (props.selectedTemplate?.parameters) {
    props.selectedTemplate.parameters.forEach(param => {
      if (!param.value || param.value.trim() === '') {
        errors[param.index] = 'Este campo é obrigatório';
        isValid = false;
      }
    });
  }

  // Validate media if template has media header
  if (hasMediaHeader.value && !isMediaValid.value) {
    errors.media =
      mediaInputType.value === 'upload'
        ? `Selecione um arquivo para o header de ${mediaTypeLabel.value.toLowerCase()}`
        : `Forneça uma URL válida (http/https) para o header de ${mediaTypeLabel.value.toLowerCase()}`;
    isValid = false;
  }

  parameterErrors.value = errors;
  return isValid;
};

// Return template params structure for API consumption
const getParameterValues = () => {
  if (!props.selectedTemplate) return {};

  const processedParams = {
    body: {},
  };
  (props.selectedTemplate.parameters || []).forEach(param => {
    const value = param.value?.trim();
    if (value) {
      processedParams.body[param.index] = value;
    }
  });

  // Include media header if template has media header
  if (hasMediaHeader.value) {
    const mediaUrlValue =
      mediaInputType.value === 'url' ? mediaUrl.value.trim() : null;

    processedParams.header = {
      media_type: headerFormat.value.toLowerCase(),
      media_url: mediaUrlValue,
    };

    // Include filename for documents
    if (headerFormat.value === 'DOCUMENT' && mediaFile.value?.name) {
      processedParams.header.media_name = mediaFile.value.name;
    }
  }

  // Get footer and buttons from template components
  const footerComp = props.selectedTemplate.components?.find(
    c => c.type === 'FOOTER'
  );
  const buttonsComp = props.selectedTemplate.components?.find(
    c => c.type === 'BUTTONS'
  );

  return {
    template_params: {
      name: props.selectedTemplate.name,
      category: props.selectedTemplate.category || 'UTILITY',
      language: props.selectedTemplate.language || 'pt_BR',
      namespace: props.selectedTemplate.namespace,
      processed_params: processedParams,
      footer: footerComp?.text || '',
      buttons: buttonsComp?.buttons || [],
    },
    header_media_file:
      mediaInputType.value === 'upload' && mediaFile.value ? mediaFile.value : null,
  };
};

// Removed complex sticky logic - using pure CSS sticky instead

// Removed onMounted/onUnmounted - no longer needed for pure CSS sticky

// Watch for inbox changes
watch(
  () => props.selectedInboxId,
  () => {
    if (props.selectedInboxId) {
      fetchTemplates();
    } else {
      templates.value = [];
    }
  },
  { immediate: true }
);

// Watch for template changes to clear media state
watch(
  () => props.selectedTemplate?.id,
  () => {
    // Clear media state when template changes
    clearMedia();
  }
);

// Expose validation function to parent component
defineExpose({
  validateParameters,
  getParameterValues,
});
</script>

<template>
  <div class="whatsapp-template-selector grid gap-6 items-start min-h-0 max-w-full" :class="props.selectedTemplate ? 'grid-cols-[3fr_2fr]' : 'grid-cols-1'">
    <!-- Left side: Template selection (dynamic width based on selection) -->
    <div class="template-selection-container flex flex-col h-full min-h-0 min-w-0">
      <!-- Message when no inbox selected -->
      <div v-if="!props.selectedInboxId" class="py-8 text-center">
        <div class="text-n-slate-11 mb-2">
          Selecione uma caixa de entrada WhatsApp primeiro
        </div>
      </div>

      <!-- Enhanced Filters -->
      <div
        v-else
        class="bg-white dark:bg-n-solid-1 dark:border-none rounded-xl p-4 mb-6 border border-n-slate-3 shadow"
      >

        <div :class="props.selectedTemplate ? 'space-y-4' : 'flex flex-wrap gap-4'">
          <!-- When template selected: Row 1 - Search bar with full width -->
          <div v-if="props.selectedTemplate" class="w-full">
            <div class="relative">
              <input
                v-model="searchQuery"
                type="text"
                class="reset-base h-[40px] border-[1px] bg-n-background pl-8 border-n-slate-4 rounded-md focus:border-woot-600 w-full"
                placeholder="Buscar templates..."
              />
              <span
                class="i-lucide-search absolute left-3 top-[23px] transform -translate-y-3 text-n-slate-11"
              />
            </div>
          </div>

          <!-- When template selected: Row 2 - Other controls -->
          <div v-if="props.selectedTemplate" class="flex flex-wrap gap-4">
            <!-- Language filter -->
            <div class="relative">
              <select
                v-model="activeLanguage"
                class="px-6 py-2 text-sm bg-n-background border border-n-slate-3 rounded-md min-w-[120px] pl-8 appearance-none m-0"
              >
                <option
                  v-for="lang in languages"
                  :key="lang.code"
                  :value="lang.code"
                >
                  {{ lang.name }}
                </option>
              </select>
              <span
                class="i-lucide-globe absolute left-2.5 top-[20px] transform -translate-y-1/2 text-blue-600 dark:text-blue-400 text-sm pointer-events-none"
              />
            </div>

            <!-- Category filter -->
            <div class="relative">
              <select
                v-model="selectedCategory"
                class="px-6 py-2 text-sm bg-n-background border border-n-slate-3 rounded-md min-w-[120px] pl-8 appearance-none m-0"
              >
                <option
                  v-for="category in categories"
                  :key="category.id"
                  :value="category.id"
                >
                  {{ category.name }}
                </option>
              </select>
              <span
                class="i-lucide-tag absolute left-2.5 top-[19px] transform -translate-y-1/2 text-purple-600 dark:text-purple-400 text-sm pointer-events-none"
              />
            </div>

            <!-- Clear filters button -->
            <button
              v-if="
                searchQuery ||
                activeLanguage !== 'all' ||
                selectedCategory !== 'all'
              "
              class="h-[40px] px-4 text-sm text-n-slate-12 bg-n-background rounded-md border border-n-slate-4 flex items-center gap-2"
              @click="
                () => {
                  searchQuery = '';
                  activeLanguage = 'all';
                  selectedCategory = 'all';
                }
              "
            >
              <i class="i-lucide-x text-sm" />
              Limpar
            </button>

            <!-- Sync templates button with tooltip -->
            <Button
              v-tooltip.top="lastUpdated ? `Última atualização: ${formatDate(lastUpdated)}` : 'Sincronizar templates do WhatsApp'"
              :label="
                isSyncing
                  ? 'Sincronizando...'
                  : canSync
                    ? 'Sincronizar'
                    : `Aguarde ${cooldownSeconds}s`
              "
              :variant="canSync ? 'solid' : 'outline'"
              color="blue"
              size="sm"
              :disabled="!canSync || isSyncing || !props.selectedInboxId"
              class="py-5 rounded-md"
              @click="syncTemplates"
            >
              <template #icon>
                <i
                  class="i-lucide-refresh-cw"
                  :class="{ 'animate-spin': isSyncing }"
                />
              </template>
            </Button>
          </div>

          <!-- When no template selected: Original single row layout -->
          <template v-else>
            <!-- Enhanced Search input -->
            <div class="relative flex-grow">
              <input
                v-model="searchQuery"
                type="text"
                class="reset-base h-[40px] border-[1px] bg-n-background pl-8 border-n-slate-4 rounded-md focus:border-woot-600 w-full"
                placeholder="Buscar templates..."
              />
              <span
                class="i-lucide-search absolute left-3 top-[23px] transform -translate-y-3 text-n-slate-11"
              />
            </div>

            <!-- Language filter -->
            <div class="relative">
              <select
                v-model="activeLanguage"
                class="px-6 py-2 text-sm bg-n-background border border-n-slate-3 rounded-md min-w-[120px] pl-8 appearance-none m-0"
              >
                <option
                  v-for="lang in languages"
                  :key="lang.code"
                  :value="lang.code"
                >
                  {{ lang.name }}
                </option>
              </select>
              <span
                class="i-lucide-globe absolute left-2.5 top-[20px] transform -translate-y-1/2 text-blue-600 dark:text-blue-400 text-sm pointer-events-none"
              />
            </div>

            <!-- Category filter -->
            <div class="relative">
              <select
                v-model="selectedCategory"
                class="px-6 py-2 text-sm bg-n-background border border-n-slate-3 rounded-md min-w-[120px] pl-8 appearance-none m-0"
              >
                <option
                  v-for="category in categories"
                  :key="category.id"
                  :value="category.id"
                >
                  {{ category.name }}
                </option>
              </select>
              <span
                class="i-lucide-tag absolute left-2.5 top-[23px] transform -translate-y-1/2 text-purple-600 dark:text-purple-400 text-sm pointer-events-none"
              />
            </div>

            <!-- Clear filters button -->
            <button
              v-if="
                searchQuery ||
                activeLanguage !== 'all' ||
                selectedCategory !== 'all'
              "
              class="h-[40px] px-4 text-sm text-n-slate-12 bg-n-background rounded-md border border-n-slate-4 flex items-center gap-2"
              @click="
                () => {
                  searchQuery = '';
                  activeLanguage = 'all';
                  selectedCategory = 'all';
                }
              "
            >
              <i class="i-lucide-x text-sm" />
              Limpar
            </button>

            <!-- Sync templates button with tooltip -->
            <Button
              v-tooltip.top="lastUpdated ? `Última atualização: ${formatDate(lastUpdated)}` : 'Sincronizar templates do WhatsApp'"
              :label="
                isSyncing
                  ? 'Sincronizando...'
                  : canSync
                    ? 'Sincronizar'
                    : `Aguarde ${cooldownSeconds}s`
              "
              :variant="canSync ? 'solid' : 'outline'"
              color="blue"
              size="sm"
              :disabled="!canSync || isSyncing || !props.selectedInboxId"
              class="py-5 rounded-md"
              @click="syncTemplates"
            >
              <template #icon>
                <i
                  class="i-lucide-refresh-cw"
                  :class="{ 'animate-spin': isSyncing }"
                />
              </template>
            </Button>
          </template>
        </div>
      </div>

      <!-- Loading state -->
      <div v-if="isLoading" class="flex justify-center items-center py-8">
        <Spinner />
      </div>

      <!-- Templates list -->
      <div
        v-else-if="filteredTemplates.length > 0"
        class="space-y-4 overflow-y-auto pr-2"
        style="max-height: 600px;"
      >
        <div
          v-for="template in filteredTemplates"
          :key="template.id"
          class="template-card group border rounded-2xl dark:border-none"
          :class="
            isTemplateSelected(template.id)
              ? 'border-n-brand'
              : 'border-slate-100'
          "
        >
          <!-- Compact Main card content -->
          <div class="p-4 cursor-pointer bg-white dark:bg-n-solid-1 hover:bg-n-slate-2 dark:hover:bg-n-slate-4" @click="selectTemplate(template)">
            <div class="flex items-center gap-3">
              <!-- Checkbox -->
              <div class="flex-shrink-0 flex items-center">
                <div
                  class="w-6 h-6 rounded-lg flex items-center justify-center shadow-sm transition-all duration-200"
                  :class="
                    isTemplateSelected(template.id)
                      ? 'bg-blue-600'
                      : 'border-2 border-n-slate-3 group-hover:border-blue-400 group-hover:bg-blue-50 dark:group-hover:bg-blue-900/20'
                  "
                  :style="
                    isTemplateSelected(template.id)
                      ? 'background-color: #2563eb !important;'
                      : ''
                  "
                >
                  <i
                    class="i-lucide-check text-xs font-bold transition-colors"
                    :class="
                      isTemplateSelected(template.id)
                        ? 'text-white'
                        : 'text-n-slate-11 group-hover:text-blue-600'
                    "
                  />
                </div>
              </div>

              <!-- Column: Title + Badges -->
              <div class="flex-1 flex flex-col gap-2 min-w-0">
                <!-- Title -->
                <h3 class="text-base font-semibold text-n-slate-12 leading-tight truncate">
                  {{ template.name }}
                </h3>

                <!-- Badges -->
                <div class="flex flex-wrap items-center gap-2">

                  <!-- Status tag -->
                  <div
                    class="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium"
                    :class="getEnhancedStatusBadgeClass(template.status)"
                  >
                    <i
                      class="mr-1 text-xs"
                      :class="getStatusIcon(template.status)"
                    />
                    {{ translateStatus(template.status) }}
                  </div>

                  <!-- Language tag -->
                  <div
                    class="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-n-slate-2 text-n-slate-10"
                  >
                    <i class="i-lucide-globe mr-1 text-xs" />
                    {{ template.language }}
                  </div>

                  <!-- Category tag -->
                  <div
                    class="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-n-slate-2 text-n-slate-10"
                  >
                    <i class="i-lucide-tag mr-1 text-xs" />
                    {{ translateCategory(template.category) }}
                  </div>
                </div>
              </div>

              <!-- Row: Parameters and buttons info -->
              <div class="flex items-center gap-4 text-xs text-n-slate-11">
                <div class="flex items-center gap-1.5">
                  <div
                    class="w-4 h-4 bg-orange-100 dark:bg-orange-900/30 rounded flex items-center justify-center"
                  >
                    <i
                      class="i-lucide-hash text-orange-600 dark:text-orange-400 text-xs"
                    />
                  </div>
                  <span class="font-medium">{{ getTemplateParameters(template).length }} param.</span>
                </div>
                <div
                  v-if="getTemplateButtons(template).length > 0"
                  class="flex items-center gap-1.5"
                >
                  <div
                    class="w-4 h-4 bg-green-100 dark:bg-green-900/30 rounded flex items-center justify-center"
                  >
                    <i
                      class="i-lucide-mouse-pointer-click text-green-600 dark:text-green-400 text-xs"
                    />
                  </div>
                  <span class="font-medium">{{ getTemplateButtons(template).length }} btn.</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div
        v-else-if="props.selectedInboxId"
        class="py-8 text-center"
      >
        <div class="w-full">
          <div class="text-n-slate-11 mb-2">
            {{
              templates.length === 0
                ? 'Nenhum template encontrado. Tente sincronizar os templates.'
                : 'Nenhum template corresponde aos seus filtros.'
            }}
          </div>
          <Button
            v-if="templates.length === 0"
            :label="
              canSync ? 'Sincronizar Templates' : `Aguarde ${cooldownSeconds}s`
            "
            variant="outline"
            color="blue"
            size="sm"
            :disabled="!canSync || isSyncing"
            @click="syncTemplates"
          />
          <Button
            v-else
            label="Limpar"
            variant="outline"
            color="slate"
            size="sm"
            @click="
              () => {
                searchQuery = '';
                activeLanguage = 'all';
                selectedCategory = 'all';
              }
            "
          />
        </div>
      </div>
    </div>

    <!-- Right side: WhatsApp preview - Only show when template is selected -->
    <div
      v-if="props.selectedTemplate"
      class="preview-container sticky top-0 min-w-0"
    >
      <div
        class="border border-n-slate-3 rounded-lg p-4 bg-white dark:bg-n-slate-1 shadow"
      >
          <div class="flex items-center gap-2 mb-1">
            <i class="i-lucide-settings-2 text-lg text-n-brand" />
            <h3 class="text-lg font-medium text-n-slate-12">
              Prévia do Template
            </h3>
          </div>

          <!-- Media Header Section -->
          <div v-if="hasMediaHeader" class="mb-6">
            <div class="flex items-center gap-2 mb-3">
              <i :class="mediaIcon" class="text-n-iris-9" />
              <span class="text-sm font-medium text-n-slate-12">
                Header de {{ mediaTypeLabel }} (obrigatório)
              </span>
            </div>

            <!-- Input Type Toggle -->
            <div v-if="!currentMediaValue" class="flex gap-2 mb-3">
              <button
                type="button"
                class="flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-md border transition-colors"
                :class="
                  mediaInputType === 'upload'
                    ? 'bg-n-iris-2 border-n-iris-6 text-n-iris-11'
                    : 'bg-transparent border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
                "
                @click="mediaInputType = 'upload'"
              >
                <i class="i-lucide-upload text-sm" />
                Upload
              </button>
              <button
                type="button"
                class="flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-md border transition-colors"
                :class="
                  mediaInputType === 'url'
                    ? 'bg-n-iris-2 border-n-iris-6 text-n-iris-11'
                    : 'bg-transparent border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
                "
                @click="mediaInputType = 'url'"
              >
                <i class="i-lucide-link text-sm" />
                URL
              </button>
            </div>

            <!-- URL Input -->
            <div v-if="mediaInputType === 'url' && !currentMediaValue" class="w-full">
              <input
                v-model="mediaUrl"
                type="url"
                class="w-full px-3 py-2 text-sm bg-n-alpha-2 dark:bg-n-solid-1 border border-n-weak rounded-lg text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:border-n-iris-6"
                placeholder="https://exemplo.com/imagem.jpg"
              />
            </div>

            <!-- Upload Input -->
            <div v-else class="w-full">
              <template v-if="currentMediaValue">
                <div class="relative mt-3 rounded-lg overflow-hidden border border-n-weak bg-n-alpha-2">
                  <div
                    v-if="isUploading"
                    class="absolute inset-0 z-10 flex items-center justify-center bg-white/70 dark:bg-n-slate-2/80 backdrop-blur-sm"
                  >
                    <div
                      class="w-6 h-6 border-2 border-n-slate-4 border-t-n-iris-9 rounded-full animate-spin"
                    />
                  </div>
                  <button
                    type="button"
                    class="absolute top-2 right-2 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 dark:bg-n-slate-2 text-n-slate-11 border border-n-weak shadow-sm hover:text-n-ruby-10 transition"
                    @click.prevent="clearMedia"
                  >
                    <i class="i-lucide-trash-2 text-base" />
                  </button>
                  <img
                    v-if="headerFormat === 'IMAGE'"
                    :src="currentMediaValue"
                    alt="Preview"
                    class="w-full max-h-48 object-contain bg-n-alpha-2"
                  />
                  <video
                    v-else-if="headerFormat === 'VIDEO'"
                    :src="currentMediaValue"
                    controls
                    class="w-full max-h-48 bg-n-alpha-2"
                  />
                  <div
                    v-else
                    class="flex items-center gap-3 p-4 bg-white/80 dark:bg-n-slate-2 text-n-slate-12"
                  >
                    <i class="i-lucide-file-text text-n-iris-9 text-xl" />
                    <div class="flex-1">
                      <p class="text-sm truncate">{{ mediaFile?.name || mediaUrl || 'Documento' }}</p>
                      <p class="text-xs text-n-slate-10">Clique na lixeira para remover</p>
                    </div>
                  </div>
                </div>
              </template>
              <template v-else>
                <label
                  class="flex flex-col items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer transition-colors"
                  :class="
                    isUploading
                      ? 'border-n-iris-6 bg-n-iris-2'
                      : 'border-n-slate-6 hover:border-n-iris-6 hover:bg-n-iris-2'
                  "
                >
                  <input
                    type="file"
                    class="hidden"
                    :accept="acceptedFileTypes"
                    @change="handleFileSelect"
                  />
                  <template v-if="isUploading">
                    <div
                      class="w-5 h-5 border-2 border-n-slate-4 border-t-n-iris-9 rounded-full animate-spin"
                    />
                    <span class="text-xs text-n-slate-11">Enviando...</span>
                  </template>
                  <template v-else>
                    <i class="i-lucide-upload text-n-slate-9 text-lg" />
                    <span class="text-xs text-n-slate-11">
                      Clique para selecionar {{ mediaTypeLabel.toLowerCase() }}
                    </span>
                    <span class="text-xs text-n-slate-10">Máximo 20MB</span>
                  </template>
                </label>
              </template>
            </div>


            <!-- Error message for media -->
            <div v-if="parameterErrors.media" class="mt-2">
              <div
                class="flex items-center gap-2 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-2 py-1 rounded-md border border-red-200 dark:border-red-800"
              >
                <i class="i-lucide-alert-circle text-xs" />
                <span>{{ parameterErrors.media }}</span>
              </div>
            </div>
          </div>

          <!-- Parameters editor -->
          <div
            v-if="
              props.selectedTemplate.parameters &&
              props.selectedTemplate.parameters.length > 0
            "
            id="template-parameters"
            class="mb-6"
          >
            <div class="flex items-center">
              <div class="mb-4">
                <p class="text-sm text-n-slate-11">
                  Personalize as variáveis do seu template
                </p>
              </div>
            </div>
            <div class="bg-gradient-to-r from-n-slate-1 to-n-slate-2 dark:from-n-slate-2 dark:to-n-slate-3 rounded-lg p-3 border border-n-slate-3 hover:border-n-brand/30 transition-all duration-200 space-y-3">
              <div
                v-for="param in props.selectedTemplate.parameters"
                :key="param.index"
              >
                <div class="flex items-center gap-2">
                  <!-- Parameter index badge -->
                  <div
                    class="flex-shrink-0 h-[42px] w-6 flex items-center justify-center"
                  >
                    <span class="text-base font-semibold text-n-slate-10">{{
                      param.index
                    }}</span>
                  </div>

                  <!-- Input field -->
                  <input
                    v-model="param.value"
                    type="text"
                    class="flex-1 h-[42px] px-3 py-2 text-sm bg-white dark:bg-n-slate-1 border rounded-lg placeholder:text-n-slate-10 text-n-slate-12 focus:outline-none focus:ring-2 transition-all duration-200 hover:border-n-slate-4 reset-base"
                    :class="[
                      parameterErrors[param.index]
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                        : 'border-n-slate-3 focus:ring-n-brand/20 focus:border-n-brand',
                    ]"
                    :placeholder="
                      param.example || `Valor para variável ${param.index}`
                    "
                  />

                  <!-- Variables button for each parameter -->
                  <div class="relative">
                    <button
                      class="h-[42px] w-[42px] bg-n-brand hover:bg-n-brand/80 rounded-lg flex items-center justify-center transition-colors duration-200"
                      title="Inserir variável"
                      @click="
                        showTemplateVariables =
                          showTemplateVariables === param.index
                            ? false
                            : param.index
                      "
                    >
                      <i class="i-lucide-braces text-white text-base" />
                    </button>

                    <!-- Variables dropdown for this parameter -->
                    <div
                      v-if="showTemplateVariables === param.index"
                      class="absolute top-full right-0 mt-1 z-20 w-72 max-h-60 overflow-y-auto bg-n-background border border-n-slate-3 rounded-lg shadow-lg"
                    >
                      <div
                        class="p-2 text-xs text-n-slate-11 border-b border-n-slate-3"
                      >
                        Clique para inserir a variável no parâmetro
                        {{ param.index }}
                      </div>
                      <div class="divide-y divide-n-slate-3">
                        <div
                          v-for="variable in templateVariables"
                          :key="variable.name"
                          class="p-2 hover:bg-n-slate-1 cursor-pointer"
                          @click="
                            insertVariable(variable.name, param.index - 1)
                          "
                        >
                          <div class="flex flex-col">
                            <span
                              class="font-mono text-n-brand text-sm font-medium"
                              >{{ variable.name }}</span>
                            <span class="text-xs text-n-slate-11 mt-1">{{
                              variable.description
                            }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Error message -->
                <div v-if="parameterErrors[param.index]" class="mt-2 px-3">
                  <div
                    class="flex items-center gap-2 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-2 py-1 rounded-md border border-red-200 dark:border-red-800"
                  >
                    <i class="i-lucide-alert-circle text-xs" />
                    <span>{{ parameterErrors[param.index] }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Quick actions -->
            <div class="mt-4 flex flex-wrap gap-2">
              <button
                class="text-xs px-3 py-1.5 bg-n-slate-2 hover:bg-n-slate-3 text-n-slate-11 hover:text-n-slate-12 rounded-md transition-colors duration-200 flex items-center gap-1"
                @click="clearAllParameters"
              >
                <i class="i-lucide-eraser text-xs" />
                Limpar Todos
              </button>
              <button
                class="text-xs px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 hover:text-blue-800 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 dark:text-blue-300 rounded-md transition-colors duration-200 flex items-center gap-1"
                @click="fillExampleParameters"
              >
                <i class="i-lucide-wand-2 text-xs" />
                Usar Exemplos
              </button>
            </div>
          </div>

          <!-- WhatsApp Preview -->
          <div class="border-t border-n-slate-3 pt-4">
            <h4 class="text-sm font-medium text-n-slate-12 mb-3">
              Visualização WhatsApp
            </h4>
            <WhatsAppPreview
              :message="processedTemplatePreview"
              :parameters="props.selectedTemplate.parameters"
              :buttons="getTemplateButtons(props.selectedTemplate)"
              :recipients="props.recipients"
              :additional-columns="props.additionalColumns"
            />
          </div>
        </div>
      </div>
    </div>
</template>

<style scoped>
/* Template preview styling in grid */
.template-preview {
  line-height: 1.5;
}

/* Use deep selectors for dynamically generated content */
.template-preview :deep(.template-header) {
  font-weight: 700;
  font-size: 1.1em;
  color: #1f2937;
  margin-bottom: 8px;
}

.dark .template-preview :deep(.template-header) {
  color: #f9fafb;
}

.template-preview :deep(.template-body) {
  color: #374151;
  margin-bottom: 8px;
  white-space: pre-wrap;
}

.dark .template-preview :deep(.template-body) {
  color: #e5e7eb;
}

.template-preview :deep(.template-footer) {
  font-size: 0.9em;
  font-weight: 300;
  color: #000000;
  opacity: 0.7;
  margin-top: 4px;
}

.dark .template-preview :deep(.template-footer) {
  color: #6b7280;
  opacity: 0.6;
}

/* WhatsApp text formatting styles */
.template-preview :deep(strong) {
  font-weight: 700;
  color: inherit;
}

.template-preview :deep(em) {
  font-style: italic;
  color: inherit;
}

.template-preview :deep(del) {
  text-decoration: line-through;
  color: inherit;
}

.template-preview :deep(code) {
  font-family: 'Courier New', monospace;
  font-size: 0.9em;
  padding: 2px 4px;
  border-radius: 3px;
}

/* Template buttons styling */
.template-preview :deep(.template-buttons) {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.template-preview :deep(.template-button) {
  padding: 6px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #f9fafb;
  font-size: 0.75em;
  font-weight: 500;
  color: #374151;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.dark .template-preview :deep(.template-button) {
  background: #374151;
  border-color: #4b5563;
  color: #e5e7eb;
}

.template-preview :deep(.template-button-reply) {
  border-color: #3b82f6;
  background: #eff6ff;
  color: #1d4ed8;
}

.dark .template-preview :deep(.template-button-reply) {
  background: #1e3a8a;
  color: #93c5fd;
}

.template-preview :deep(.template-button-url) {
  border-color: #10b981;
  background: #ecfdf5;
  color: #047857;
}

.dark .template-preview :deep(.template-button-url) {
  background: #064e3b;
  color: #6ee7b7;
}

.template-preview :deep(.template-button-phone) {
  border-color: #f59e0b;
  background: #fffbeb;
  color: #d97706;
}

.dark .template-preview :deep(.template-button-phone) {
  background: #78350f;
  color: #fcd34d;
}

/* Enhanced Template card styling */
.template-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  position: relative;
}

.dark .template-card {
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
}

/* Smooth expand/collapse animation */
.template-preview {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Template preview content width limit */
.template-preview-content {
  max-width: 400px;
}

/* Template selection container */
.template-selection-container {
  transition: all 0.3s ease;
}

.preview-container {
  transition: all 0.3s ease;
  z-index: 50;
  position: relative;
}
</style>
